import { getSupabaseBrowserClient } from "@/lib/supabaseBrowser";

export const API_URL = import.meta.env.VITE_API_URL;

interface RequestOptions extends RequestInit {
  requiresAuth?: boolean;
}

class ApiService {
  private baseURL: string;

  /** Single-flight refresh so parallel 401s do not stampede Supabase. */
  private authRefreshPromise: Promise<boolean> | null = null;

  /** After auth/me fails for CSRF bootstrap, avoid hammering the API every POST heartbeat. */
  private csrfAuthBackoffUntil = 0;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  /**
   * Supabase keeps the canonical session (incl. auto-refresh). Our `sb-access-token` keys are
   * only updated on login/sync — prefer the live session so REST Bearer matches a valid JWT.
   */
  private mirrorSupabaseSessionToAppStorage(session: {
    access_token: string;
    refresh_token: string;
  }): void {
    const inLocal =
      localStorage.getItem("sb-access-token") != null ||
      localStorage.getItem("sb-refresh-token") != null;
    const inSession =
      sessionStorage.getItem("sb-access-token") != null ||
      sessionStorage.getItem("sb-refresh-token") != null;

    if (inLocal) {
      localStorage.setItem("sb-access-token", session.access_token);
      localStorage.setItem("sb-refresh-token", session.refresh_token);
    }
    if (inSession) {
      sessionStorage.setItem("sb-access-token", session.access_token);
      sessionStorage.setItem("sb-refresh-token", session.refresh_token);
    }
    if (!inLocal && !inSession) {
      localStorage.setItem("sb-access-token", session.access_token);
      localStorage.setItem("sb-refresh-token", session.refresh_token);
    }
  }

  private rememberMeForSyncSession(): boolean {
    return localStorage.getItem("sb-refresh-token") != null;
  }

  /** Updates httpOnly cookies via public sync-session (no Bearer required). */
  private async syncSessionCookies(session: {
    access_token: string;
    refresh_token: string;
    expires_at?: number;
    expires_in?: number;
  }): Promise<void> {
    const rememberMe = this.rememberMeForSyncSession();
    const body: Record<string, unknown> = {
      access_token: session.access_token,
      refresh_token: session.refresh_token,
      rememberMe,
    };
    if (session.expires_at != null) body.expires_at = session.expires_at;
    if (session.expires_in != null) body.expires_in = session.expires_in;

    await fetch(`${this.baseURL}/api/v1/auth/sync-session`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  }

  private refreshSessionAndSync(): Promise<boolean> {
    if (!this.authRefreshPromise) {
      this.authRefreshPromise = (async () => {
        try {
          const supabase = getSupabaseBrowserClient();
          const { data, error } = await supabase.auth.refreshSession();
          if (error || !data.session) {
            if (import.meta.env.DEV) {
              console.warn("[ApiService] refreshSession failed:", error?.message);
            }
            return false;
          }
          this.mirrorSupabaseSessionToAppStorage(data.session);
          await this.syncSessionCookies(data.session);
          return true;
        } catch (e) {
          if (import.meta.env.DEV) {
            console.warn("[ApiService] refreshSession error:", e);
          }
          return false;
        }
      })().finally(() => {
        this.authRefreshPromise = null;
      });
    }
    return this.authRefreshPromise;
  }

  private async resolveAuthBearerToken(): Promise<string | null> {
    try {
      const {
        data: { session },
      } = await getSupabaseBrowserClient().auth.getSession();
      if (session?.access_token) {
        const stored =
          localStorage.getItem("sb-access-token") ||
          sessionStorage.getItem("sb-access-token");
        if (stored !== session.access_token) {
          this.mirrorSupabaseSessionToAppStorage(session);
        }
        return session.access_token;
      }
    } catch {
      /* missing VITE_SUPABASE_* or SSR */
    }

    return (
      this.getCookie("sb-access-token") ||
      localStorage.getItem("sb-access-token") ||
      sessionStorage.getItem("sb-access-token") ||
      this.getCookie("accessToken") ||
      localStorage.getItem("accessToken") ||
      sessionStorage.getItem("accessToken") ||
      null
    );
  }

  // Helper to get cookie value
  private getCookie(name: string): string | null {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop()?.split(";").shift() || null;
    }
    return null;
  }

  /**
   * Fetch a fresh CSRF token by making a GET request
   *
   * Security Note: We rely on cookies (set by backend) as the primary source.
   * Cookies are more secure than localStorage as they're automatically sent
   * with requests and can be protected with httpOnly (though CSRF tokens need
   * to be readable by JS for Double Submit Cookie pattern).
   *
   * @returns CSRF token from cookie or header, null if unavailable
   */
  private async fetchCsrfToken(): Promise<string | null> {
    try {
      const now = Date.now();
      if (now < this.csrfAuthBackoffUntil) {
        return this.getCookie("csrf-token");
      }

      const bearer = (await this.resolveAuthBearerToken()) || "";
      // Make a lightweight GET request to trigger CSRF token generation
      // The backend will set the token in both cookie and response header
      const response = await fetch(`${this.baseURL}/api/v1/auth/me`, {
        method: "GET",
        credentials: "include", // Essential: Include cookies in request
        headers: bearer ? { Authorization: `Bearer ${bearer}` } : {},
      });

      if (response.status === 401 || response.status === 403) {
        this.csrfAuthBackoffUntil = now + 60_000;
        return null;
      }

      // Primary: Read from cookie (set automatically by backend via Set-Cookie header)
      // This is more secure than localStorage as cookies are automatically managed by browser
      const csrfTokenFromCookie = this.getCookie("csrf-token");

      // Fallback: Read from response header (if cookie reading fails)
      const csrfTokenFromHeader = response.headers.get("X-CSRF-Token");

      // Prefer cookie over header (more secure, automatically managed)
      return csrfTokenFromCookie || csrfTokenFromHeader || null;
    } catch (error) {
      // Don't log in production to avoid exposing internal errors
      if (import.meta.env.DEV) {
        console.warn("[ApiService] Failed to fetch CSRF token:", error);
      }
      return null;
    }
  }

  private async request<T>(
    endpoint: string,
    options: RequestOptions = {},
    retryOnCsrfError = true,
    retryOnJwtRefresh = true
  ): Promise<T> {
    const { requiresAuth = true, ...fetchOptions } = options;

    // Don't set Content-Type for FormData - browser will set it with boundary
    const isFormData = fetchOptions.body instanceof FormData;
    const headers: Record<string, string> = isFormData
      ? { ...(fetchOptions.headers as Record<string, string>) }
      : {
          "Content-Type": "application/json",
          ...(fetchOptions.headers as Record<string, string>),
        };

    // Add authorization header if required
    // Support both Supabase session tokens and legacy JWT tokens
    if (requiresAuth) {
      const bearer = await this.resolveAuthBearerToken();
      if (bearer) {
        headers["Authorization"] = `Bearer ${bearer}`;
      }
    }

    // Add CSRF token for state-changing requests (POST, PUT, DELETE, PATCH)
    // Using Double Submit Cookie pattern: token is set in cookie by backend, we send it in header
    const method = (fetchOptions.method || "GET").toUpperCase();
    if (["POST", "PUT", "DELETE", "PATCH"].includes(method)) {
      // Security: Rely primarily on cookies (set automatically by backend)
      // Cookies are more secure than localStorage:
      // - Automatically sent with requests (credentials: "include")
      // - Can be protected with SameSite attribute
      // - Less vulnerable to XSS attacks (though CSRF tokens must be readable by JS)
      let csrfToken = this.getCookie("csrf-token");

      // Only fetch if token is missing (cookies should always be available in normal operation)
      // This handles edge cases like first request or cookie expiration
      if (!csrfToken && requiresAuth) {
        csrfToken = await this.fetchCsrfToken();
      }

      if (csrfToken) {
        headers["X-CSRF-Token"] = csrfToken;
      } else if (requiresAuth) {
        // Log warning if we can't get CSRF token for authenticated requests
        // This helps identify configuration issues (CORS, cookie settings, etc.)
        if (import.meta.env.DEV) {
          console.warn(
            "[ApiService] No CSRF token available for authenticated request. " +
              "Check CORS and cookie configuration."
          );
        }
      }
    }

    // Create abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        ...fetchOptions,
        headers,
        signal: controller.signal,
        credentials: "include", // Important: Include cookies in cross-origin requests
      });

      clearTimeout(timeoutId);

      if (requiresAuth && response.ok) {
        this.csrfAuthBackoffUntil = 0;
      }

      // Extract CSRF token from response headers (for GET requests)
      // Note: The backend automatically sets the token in a cookie via Set-Cookie header
      // We don't need to manually store it - the browser handles cookies automatically
      // This is more secure than localStorage as cookies are automatically managed
      // and can be protected with SameSite, Secure, and HttpOnly attributes

      // The token in the header is informational - the cookie is the source of truth
      // We verify the cookie exists to ensure proper backend configuration
      if (import.meta.env.DEV && response.headers.get("X-CSRF-Token")) {
        const cookieToken = this.getCookie("csrf-token");
        if (!cookieToken) {
          console.warn(
            "[ApiService] CSRF token in header but not in cookie. " +
              "Check cookie configuration (SameSite, Secure, Domain)."
          );
        }
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          message: response.statusText,
          statusCode: response.status,
          success: false,
        }));

        // Handle CSRF token errors by fetching a new token and retrying once
        // This handles cases where:
        // - Token expired (backend restarted, token expired after 1 hour)
        // - Token invalid (server-side token store cleared)
        //
        // Security: Only retry on specific CSRF validation errors, not all auth errors
        // This prevents masking other authentication issues
        const isCsrfError =
          response.status === 401 &&
          errorData.code === "AUTH_INVALID_TOKEN" &&
          errorData.context?.metadata?.action === "csrf_validation";

        if (
          isCsrfError &&
          retryOnCsrfError &&
          ["POST", "PUT", "DELETE", "PATCH"].includes(method)
        ) {
          // Fetch a fresh CSRF token (this will be set in cookie by backend)
          const freshToken = await this.fetchCsrfToken();
          if (freshToken) {
            // Small delay to ensure cookie is properly set by browser
            await new Promise((resolve) => setTimeout(resolve, 100));

            // Verify the token is now in the cookie before retrying
            const verifiedToken = this.getCookie("csrf-token");
            if (verifiedToken) {
              // Retry the request with the fresh token (only once to prevent infinite loops)
              return this.request<T>(endpoint, options, false, retryOnJwtRefresh);
            } else {
              // Cookie wasn't set properly, log warning
              if (import.meta.env.DEV) {
                console.warn(
                  "[ApiService] CSRF token fetched but not found in cookie. " +
                    "This may indicate a cookie configuration issue (SameSite, Secure, Domain)."
                );
              }
            }
          } else {
            // If we can't get a fresh token, the issue is likely authentication-related
            // Don't mask this - let the original error propagate
            if (import.meta.env.DEV) {
              console.error(
                "[ApiService] CSRF token refresh failed. " +
                  "This may indicate an authentication or configuration issue."
              );
            }
          }
        }

        const isJwtAuthError =
          requiresAuth &&
          retryOnJwtRefresh &&
          response.status === 401 &&
          (errorData.code === "AUTH_INVALID_TOKEN" ||
            errorData.code === "AUTH_TOKEN_EXPIRED") &&
          !isCsrfError;

        if (isJwtAuthError) {
          const refreshed = await this.refreshSessionAndSync();
          if (refreshed) {
            return this.request<T>(endpoint, options, retryOnCsrfError, false);
          }
        }

        // In production, avoid surfacing server/infrastructure details on 5xx
        const status = response.status;
        const safeUserMessage =
          import.meta.env.PROD && status >= 500
            ? "Something went wrong. Please try again."
            : errorData.message || `HTTP ${status}: ${response.statusText}`;

        const error = new Error(safeUserMessage);
        (error as any).response = {
          data: {
            ...errorData,
            statusCode: errorData.statusCode || status,
            success: false,
          },
          status,
        };
        throw error;
      }

      const json = await response.json();

      if (json && typeof json === "object") {
        const hasDataProp = Object.prototype.hasOwnProperty.call(json, "data");
        const hasSuccessProp = Object.prototype.hasOwnProperty.call(json, "success");

        if (hasDataProp && hasSuccessProp) {
          if (!json.success) {
            throw new Error(json.message || "Request failed");
          }
          return json.data as T;
        }
      }

      return json as T;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error && error.name === "AbortError") {
        throw new Error("The request timed out. Please try again or contact support if the issue persists.");
      }
      throw error;
    }
  }

  // GET request
  async get<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: "GET" });
  }

  // POST request
  async post<T>(endpoint: string, data?: any, options?: RequestOptions): Promise<T> {
    // Don't stringify FormData - pass it directly
    const body = data instanceof FormData ? data : data ? JSON.stringify(data) : undefined;
    return this.request<T>(endpoint, {
      ...options,
      method: "POST",
      body,
    });
  }

  // PUT request
  async put<T>(endpoint: string, data?: any, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: "PUT",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // PATCH request
  async patch<T>(endpoint: string, data?: any, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: "PATCH",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // DELETE request
  async delete<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: "DELETE" });
  }
}

// Create singleton instance
export const apiService = new ApiService(API_URL);

// Auth endpoints
export const authAPI = {
  login: (email: string, password: string, rememberMe = true) =>
    apiService.post<{
      user: any;
      company: any;
      session: { access_token: string; refresh_token: string };
      paymentRequired: boolean;
      onboardingRequired: boolean;
      needsCompanySetup: boolean;
    }>(
      "/api/v1/auth/login",
      { email, password, rememberMe },
      { requiresAuth: false }
    ),

  /**
   * After Supabase OAuth/SSO redirect — sets server cookies and same side effects as login.
   */
  syncSession: (body: {
    access_token: string;
    refresh_token: string;
    rememberMe?: boolean;
    expires_at?: number;
    expires_in?: number;
  }) =>
    apiService.post<{
      user: any;
      company: any;
      session: {
        access_token: string;
        refresh_token: string;
        expires_at?: number;
        expires_in?: number;
        token_type?: string;
      };
      paymentRequired: boolean;
      onboardingRequired: boolean;
      needsCompanySetup: boolean;
    }>("/api/v1/auth/sync-session", body, { requiresAuth: false }),

  register: (data: { name: string; email: string; password: string }) =>
    apiService.post<{
      user: any;
      company: any;
      requiresVerification: boolean;
      message: string;
    }>("/api/v1/auth/register", data, { requiresAuth: false }),

  /** Call after Supabase session is established from /verify-email (hash or PKCE). Sends welcome email once. */
  completeEmailVerification: () =>
    apiService.post<{ welcomeEmailSent: boolean }>(
      "/api/v1/auth/complete-email-verification",
      {},
      { requiresAuth: true }
    ),

  /** OAuth users without a company: create org + widget (after Google/GitHub sign-in). */
  oauthCompleteCompany: (data: {
    companyName: string;
    website?: string;
    billingEmail?: string;
  }) =>
    apiService.post<{
      user: any;
      company: any;
      paymentRequired: boolean;
      onboardingRequired: boolean;
      needsCompanySetup: boolean;
    }>("/api/v1/auth/oauth-complete-company", data, { requiresAuth: true }),

  resendVerification: (email: string) =>
    apiService.post("/api/v1/auth/resend-verification", { email }, { requiresAuth: false }),

  forgotPassword: (email: string) =>
    apiService.post("/api/v1/auth/forgot-password", { email }, { requiresAuth: false }),

  resetPassword: (token: string, password: string) =>
    apiService.post("/api/v1/auth/reset-password", { token, password }, { requiresAuth: false }),

  logout: () => apiService.post("/api/v1/auth/logout", {}),

  getMe: () =>
    apiService.get<{
      user: any;
      company: any;
      paymentRequired: boolean;
      onboardingRequired: boolean;
      needsCompanySetup: boolean;
    }>("/api/v1/auth/me"),
};

// User Settings endpoints
export const userSettingsAPI = {
  uploadProfilePicture: (file: File) => {
    const formData = new FormData();
    formData.append("profilePicture", file);

    // Use post method which now handles FormData properly
    return apiService.post<{ user: { id: string; profile_picture: string } }>(
      "/api/v1/user-settings/profile-picture",
      formData
    );
  },

  uploadCompanyLogo: (file: File) => {
    const formData = new FormData();
    formData.append("logo", file);
    return apiService.post<{
      company: { id: string; logo: string; logoUrl: string };
    }>("/api/v1/user-settings/company-logo", formData);
  },

  uploadWidgetAvatar: (file: File) => {
    const formData = new FormData();
    formData.append("avatar", file);
    return apiService.post<{ avatarImageUrl: string }>(
      "/api/v1/user-settings/widget-avatar",
      formData
    );
  },

  uploadWidgetLogo: (file: File) => {
    const formData = new FormData();
    formData.append("logo", file);
    return apiService.post<{ logoUrl: string; logoPath?: string }>(
      "/api/v1/user-settings/widget-logo",
      formData
    );
  },

  updateProfile: (data: { name?: string; email?: string }) =>
    apiService.put<{ user: any }>("/api/v1/user-settings/profile", data),

  getUserPreferences: () =>
    apiService.get<{ preferences: any }>("/api/v1/user-settings/preferences"),

  updateUserPreferences: (data: {
    theme?: "light" | "dark" | "auto";
    language?: string;
    timezone?: string;
    date_format?: string;
    time_format?: "12h" | "24h";
    dashboard_layout?: Record<string, any>;
    preferences?: Record<string, any>;
  }) => apiService.put<{ preferences: any }>("/api/v1/user-settings/preferences", data),

  getNotificationPreferences: () =>
    apiService.get<{ preferences: any }>("/api/v1/user-settings/notification-preferences"),

  updateNotificationPreferences: (data: {
    email_enabled?: boolean;
    push_enabled?: boolean;
    in_app_enabled?: boolean;
    email_frequency?: "REALTIME" | "DAILY" | "WEEKLY" | "NEVER";
    preferences?: Record<string, any>;
  }) =>
    apiService.put<{ preferences: any }>("/api/v1/user-settings/notification-preferences", data),
};

// Visitor endpoints
export const visitorsAPI = {
  getAll: (params?: { page?: number; limit?: number }) =>
    apiService.get<{ visitors: any[]; total: number }>(
      `/api/v1/visitors${params ? `?page=${params.page}&limit=${params.limit}` : ""}`
    ),

  getActive: () => apiService.get<{ visitors: any[] }>("/api/v1/visitors/active"),

  getById: (id: string) => apiService.get<{ visitor: any }>(`/api/v1/visitors/${id}`),
};

// Call endpoints
export const callsAPI = {
  getAll: (params?: {
    status?: string;
    startDate?: string;
    endDate?: string;
    salesRepId?: string;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append("status", params.status);
    if (params?.startDate) queryParams.append("startDate", params.startDate);
    if (params?.endDate) queryParams.append("endDate", params.endDate);
    if (params?.salesRepId) queryParams.append("salesRepId", params.salesRepId);
    const query = queryParams.toString();
    return apiService.get<{ calls: any[] }>(`/api/v1/calls${query ? `?${query}` : ""}`);
  },

  getById: (id: string) => apiService.get<{ call: any }>(`/api/v1/calls/${id}`),

  initiate: (visitorId: string, callType: "video" | "voice") =>
    apiService.post<{ call: any }>("/api/v1/calls/initiate", { visitorId, callType }),

  complete: (id: string, duration: number, notes?: string) =>
    apiService.post<{ call: any }>(`/api/v1/calls/${id}/complete`, { duration, notes }),

  markMeetingBooked: (id: string) =>
    apiService.patch<{ call: any }>(`/api/v1/calls/${id}/meeting-booked`, {}),
};

// Calendar endpoints
export const calendarAPI = {
  getData: (params: { startDate: string; endDate: string; salesRepId?: string }) => {
    const q = new URLSearchParams(params as Record<string, string>);
    return apiService.get<{
      summary: Array<{ date: string; totalCalls: number; completedCalls: number; meetingsBooked: number; totalDuration: number }>;
      calls: any[];
      events: any[];
      stats: { totalCalls: number; meetingsBooked: number; completedCalls: number; totalDuration: number };
    }>(`/api/v1/calendar?${q}`);
  },

  getUpcoming: (limit?: number, salesRepId?: string) => {
    const q = new URLSearchParams();
    if (limit) q.append("limit", String(limit));
    if (salesRepId) q.append("salesRepId", salesRepId);
    return apiService.get<{ meetings: any[]; count: number }>(`/api/v1/calendar/upcoming?${q}`);
  },

  getDayCalls: (date: string, salesRepId?: string) => {
    const q = salesRepId ? `?salesRepId=${salesRepId}` : "";
    return apiService.get<{ calls: any[]; events: any[]; count: number; date: string }>(`/api/v1/calendar/day/${date}${q}`);
  },

  scheduleMeeting: (callId: string, scheduledAt: string, notes?: string, syncToCalendar?: "google" | "outlook", title?: string) =>
    apiService.put<{ call: any; externalEventId?: string }>(`/api/v1/calendar/${callId}/schedule`, { scheduledAt, notes, title, syncToCalendar }),

  unscheduleMeeting: (callId: string) =>
    apiService.delete<{ call: any }>(`/api/v1/calendar/${callId}/meeting`),

  createEvent: (event: { title: string; description?: string; startTime: string; endTime: string; color?: string; syncToCalendar?: "google" | "outlook" }) =>
    apiService.post<{ event: any; externalEventId?: string }>("/api/v1/calendar/events", event),

  updateEvent: (id: string, event: { title: string; description?: string; startTime: string; endTime: string }) =>
    apiService.put<{ event: any }>(`/api/v1/calendar/events/${id}`, event),

  deleteEvent: (id: string) =>
    apiService.delete(`/api/v1/calendar/events/${id}`),

  getSyncStatus: () =>
    apiService.get<{
      google: { connected: boolean; configured: boolean };
      outlook: { connected: boolean; configured: boolean };
    }>("/api/v1/calendar/sync/status"),

  initSync: (provider: "google" | "outlook") =>
    apiService.post<{ authUrl: string; provider: string }>(`/api/v1/calendar/sync/${provider}`),

  disconnectSync: (provider: "google" | "outlook") =>
    apiService.delete(`/api/v1/calendar/sync/${provider}`),
};

// Analytics endpoints
export const analyticsAPI = {
  getCallStats: (startDate?: string, endDate?: string) => {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    const query = params.toString();
    return apiService.get<{ stats: any }>(`/api/v1/analytics/calls${query ? `?${query}` : ""}`);
  },

  getVisitorStats: (startDate?: string, endDate?: string) => {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    const query = params.toString();
    return apiService.get<{ stats: any }>(`/api/v1/analytics/visitors${query ? `?${query}` : ""}`);
  },

  getConversionStats: (startDate?: string, endDate?: string) => {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    const query = params.toString();
    return apiService.get<{ stats: any }>(
      `/api/v1/analytics/conversion${query ? `?${query}` : ""}`
    );
  },

  getSalesRepPerformance: () =>
    apiService.get<{ performance: any[] }>("/api/v1/analytics/sales-reps"),
};

// Payment endpoints
export const paymentAPI = {
  selectPlan: (
    plan: "PAY_AS_YOU_GO" | "BUSINESS_PRO" | "ENTERPRISE",
    billingInterval?: "monthly" | "annual"
  ) =>
    apiService.post<{
      company: {
        id: string;
        name: string;
        plan: string;
        stripeCustomerId: string;
      };
      plan: "PAY_AS_YOU_GO" | "BUSINESS_PRO" | "ENTERPRISE";
      hasSubscription: boolean;
      requiresPaymentMethod?: boolean;
      checkoutSession?: {
        id: string;
        url: string;
      };
    }>("/api/v1/payment/select-plan", {
      plan,
      billingInterval: billingInterval || "monthly",
    }),

  createPaymentMethodSession: (returnUrl: string) =>
    apiService.post<{
      checkoutSession: {
        id: string;
        url: string;
      };
      hasPaymentMethod?: boolean;
    }>("/api/v1/payment/create-payment-method-session", { returnUrl }),

  /** Same logic as public widget config: when inactive + requires payment, embed will not load. */
  getPaymentStatus: () =>
    apiService.get<{
      isActive: boolean;
      hasPaymentMethod: boolean;
      requiresPayment: boolean;
      paymentRequiredReason?: string;
      subscriptionStatus?: string;
      subscriptionEndDate?: string;
      lastPaymentDate?: string;
      gracePeriodEnd?: string;
    }>("/api/v1/payment/status"),

  getBillingInfo: () =>
    apiService.get<{
      subscription: {
        hasSubscription: boolean;
        isActive: boolean;
        plan: string | null;
        currentPeriodEnd: Date | null;
        cancelAtPeriodEnd: boolean;
      };
      paymentMethod: {
        brand: string;
        last4: string;
        exp_month: number;
        exp_year: number;
      } | null;
      pricing: {
        plan: string;
        amount: number;
        period: string;
      };
      company: {
        id: string;
        name: string;
        plan: string;
        status: string;
      };
    }>("/api/v1/payment/billing-info"),

  getPaymentHistory: (params?: { limit?: number; offset?: number; status?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.offset) queryParams.append("offset", params.offset.toString());
    if (params?.status) queryParams.append("status", params.status);
    const query = queryParams.toString();
    return apiService.get<{
      payments: any[];
      total: number;
      limit: number;
      offset: number;
    }>(`/api/v1/payment/history${query ? `?${query}` : ""}`);
  },

  getInvoices: (params?: { limit?: number; offset?: number }) => {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.offset) queryParams.append("offset", params.offset.toString());
    const query = queryParams.toString();
    return apiService.get<{
      invoices: any[];
      total: number;
      limit: number;
      offset: number;
    }>(`/api/v1/payment/invoices${query ? `?${query}` : ""}`);
  },

  cancelSubscription: () =>
    apiService.post<{
      subscription: {
        id: string;
        cancelAtPeriodEnd: boolean;
      };
    }>("/api/v1/payment/cancel-subscription", {}),
};

// Onboarding endpoints
export const onboardingAPI = {
  getStatus: () =>
    apiService.get<{
      onboarding: {
        isComplete: boolean;
        currentStep: string;
        steps: Array<{
          id: string;
          title: string;
          description: string;
          completed: boolean;
          required: boolean;
        }>;
        progress: number;
      };
      widget: {
        apiKey: string;
        isActive: boolean;
        integrationCode: string;
      } | null;
    }>("/api/v1/onboarding/status"),

  completeStep: (step: string, data?: Record<string, unknown>) =>
    apiService.post<{
      onboarding: {
        isComplete: boolean;
        currentStep: string;
        progress: number;
      };
    }>("/api/v1/onboarding/complete-step", { step, data }),

  getWidgetIntegrationGuide: () =>
    apiService.get<{
      apiKey: string;
      integrationCode: string;
      steps: Array<{
        step: number;
        title: string;
        description: string;
        code?: string;
      }>;
      platformGuides: Record<
        string,
        {
          title: string;
          steps: string[];
        }
      >;
    }>("/api/v1/onboarding/widget-integration"),
};

// Admin endpoints (for customer admins)
export const adminAPI = {
  getUsers: () => apiService.get<{ users: any[] }>("/api/v1/admin/users"),

  addSalesRep: (name: string, email: string, roleLabel?: string | null) =>
    apiService.post<{ user: any }>("/api/v1/admin/users", {
      name,
      email,
      ...(roleLabel != null && roleLabel.trim() !== "" ? { role_label: roleLabel.trim() } : {}),
    }),

  updateUser: (id: string, data: any) =>
    apiService.put<{ user: any }>(`/api/v1/admin/users/${id}`, data),

  deleteUser: (id: string) => apiService.delete<{ success: boolean }>(`/api/v1/admin/users/${id}`),

  getWidgetSettings: () => apiService.get<{ widget: any }>("/api/v1/admin/widget-settings"),

  updateWidgetSettings: (settings: any) =>
    apiService.put<{ settings: any }>("/api/v1/admin/widget-settings", settings),

  // Trial (for company admins)
  getCompanyTrial: () =>
    apiService.get<{ trial: any }>("/api/v1/admin/trial"),
};

// Platform Admin endpoints (for SUPER_ADMIN - Reacho team)
const PLATFORM_ADMIN_PREFIX = "/api/v1/platform-admin";
export const platformAdminAPI = {
  // Companies
  getCompanies: (params?: { page?: number; limit?: number; search?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.search) queryParams.append("search", params.search);
    const query = queryParams.toString();
    return apiService.get<{ companies: any[]; total: number }>(
      `${PLATFORM_ADMIN_PREFIX}/companies${query ? `?${query}` : ""}`
    );
  },

  getCompanyById: (id: string) =>
    apiService.get<{ company: any }>(`${PLATFORM_ADMIN_PREFIX}/companies/${id}`),

  updateCompany: (id: string, data: any) =>
    apiService.put<{ company: any }>(`${PLATFORM_ADMIN_PREFIX}/companies/${id}`, data),

  deleteCompany: (id: string) =>
    apiService.delete<{ success: boolean }>(`${PLATFORM_ADMIN_PREFIX}/companies/${id}`),

  // Platform Analytics
  getPlatformStats: (startDate?: string, endDate?: string) => {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    const query = params.toString();
    return apiService.get<{ stats: any }>(
      `${PLATFORM_ADMIN_PREFIX}/analytics/platform${query ? `?${query}` : ""}`
    );
  },

  getCompanyStats: (companyId: string, startDate?: string, endDate?: string) => {
    const params = new URLSearchParams();
    params.append("companyId", companyId);
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    return apiService.get<{ stats: any }>(
      `${PLATFORM_ADMIN_PREFIX}/analytics/company?${params.toString()}`
    );
  },

  getEnrichmentHealth: () =>
    apiService.get<{
      provider: string;
      isEnabled: boolean;
      config: {
        hasApiKey: boolean;
        hasWebhookSecret: boolean;
        hasWebhookHmacSecret: boolean;
        apiBaseUrl: string | null;
        enrichPath: string | null;
        timeoutMs: number;
        successTtlMs: number;
        retryTtlMinutes: number;
        dailyBudgetPerCompany: number;
        estimatedCostPerRequest: number;
      };
      stats: {
        attemptsToday: number;
        successToday: number;
        attemptsLastHour: number;
        successLastHour: number;
        successRateToday: number;
      };
      runtimeMetrics: Record<string, number>;
      recentCache: any[];
    }>(`${PLATFORM_ADMIN_PREFIX}/enrichment/health`),

  getAllCalls: (params?: {
    companyId?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.companyId) queryParams.append("companyId", params.companyId);
    if (params?.status) queryParams.append("status", params.status);
    if (params?.startDate) queryParams.append("startDate", params.startDate);
    if (params?.endDate) queryParams.append("endDate", params.endDate);
    const query = queryParams.toString();
    return apiService.get<{ calls: any[]; total: number }>(
      `${PLATFORM_ADMIN_PREFIX}/calls${query ? `?${query}` : ""}`
    );
  },

  getAllVisitors: (params?: { companyId?: string; page?: number; limit?: number }) => {
    const queryParams = new URLSearchParams();
    if (params?.companyId) queryParams.append("companyId", params.companyId);
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    const query = queryParams.toString();
    return apiService.get<{ visitors: any[]; total: number }>(
      `${PLATFORM_ADMIN_PREFIX}/visitors${query ? `?${query}` : ""}`
    );
  },

  // Support
  getSupportTickets: (params?: { status?: string; page?: number; limit?: number }) => {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append("status", params.status);
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    const query = queryParams.toString();
    return apiService.get<{ tickets: any[]; total: number }>(
      `${PLATFORM_ADMIN_PREFIX}/support/tickets${query ? `?${query}` : ""}`
    );
  },

  updateSupportTicket: (id: string, data: any) =>
    apiService.put<{ ticket: any }>(`${PLATFORM_ADMIN_PREFIX}/support/tickets/${id}`, data),

  // Billing Oversight
  getBillingOverview: () =>
    apiService.get<{ overview: any }>(`${PLATFORM_ADMIN_PREFIX}/billing/overview`),

  getCompanyBilling: (companyId: string) =>
    apiService.get<{ billing: any }>(`${PLATFORM_ADMIN_PREFIX}/billing/companies/${companyId}`),

  // Users (all platform users)
  getAllUsers: (params?: { companyId?: string; role?: string; page?: number; limit?: number }) => {
    const queryParams = new URLSearchParams();
    if (params?.companyId) queryParams.append("companyId", params.companyId);
    if (params?.role) queryParams.append("role", params.role);
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    const query = queryParams.toString();
    return apiService.get<{ users: any[]; total: number }>(
      `${PLATFORM_ADMIN_PREFIX}/users${query ? `?${query}` : ""}`
    );
  },

  // Discount Codes
  getDiscountCodes: (params?: { page?: number; limit?: number; is_active?: boolean; created_by?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.is_active !== undefined) queryParams.append("is_active", params.is_active.toString());
    if (params?.created_by) queryParams.append("created_by", params.created_by);
    const query = queryParams.toString();
    return apiService.get<{ discount_codes: any[]; pagination: any }>(
      `${PLATFORM_ADMIN_PREFIX}/discounts${query ? `?${query}` : ""}`
    );
  },

  getDiscountCodeById: (id: string) =>
    apiService.get<{ discount_code: any; statistics: any }>(`${PLATFORM_ADMIN_PREFIX}/discounts/${id}`),

  createDiscountCode: (data: any) =>
    apiService.post<{ discount_code: any }>(`${PLATFORM_ADMIN_PREFIX}/discounts`, data),

  updateDiscountCode: (id: string, data: any) =>
    apiService.put<{ discount_code: any }>(`${PLATFORM_ADMIN_PREFIX}/discounts/${id}`, data),

  deleteDiscountCode: (id: string) =>
    apiService.delete<{ success: boolean }>(`${PLATFORM_ADMIN_PREFIX}/discounts/${id}`),

  getDiscountCodeUsage: (id: string, params?: { page?: number; limit?: number }) => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    const query = queryParams.toString();
    return apiService.get<{ usage: any[]; pagination: any }>(
      `${PLATFORM_ADMIN_PREFIX}/discounts/${id}/usage${query ? `?${query}` : ""}`
    );
  },

  // Referral Codes
  getReferralCodes: (params?: { page?: number; limit?: number; is_active?: boolean; referrer_user_id?: string; referrer_company_id?: string; campaign_id?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.is_active !== undefined) queryParams.append("is_active", params.is_active.toString());
    if (params?.referrer_user_id) queryParams.append("referrer_user_id", params.referrer_user_id);
    if (params?.referrer_company_id) queryParams.append("referrer_company_id", params.referrer_company_id);
    if (params?.campaign_id) queryParams.append("campaign_id", params.campaign_id);
    const query = queryParams.toString();
    return apiService.get<{ referral_codes: any[]; pagination: any }>(
      `${PLATFORM_ADMIN_PREFIX}/referrals${query ? `?${query}` : ""}`
    );
  },

  getReferralCodeById: (id: string) =>
    apiService.get<{ referral_code: any; statistics: any }>(`${PLATFORM_ADMIN_PREFIX}/referrals/${id}`),

  createReferralCode: (data: any) =>
    apiService.post<{ referral_code: any }>(`${PLATFORM_ADMIN_PREFIX}/referrals`, data),

  updateReferralCode: (id: string, data: any) =>
    apiService.put<{ referral_code: any }>(`${PLATFORM_ADMIN_PREFIX}/referrals/${id}`, data),

  deleteReferralCode: (id: string) =>
    apiService.delete<{ success: boolean }>(`${PLATFORM_ADMIN_PREFIX}/referrals/${id}`),

  // Trials
  getTrials: (params?: { page?: number; limit?: number; status?: string; trial_type?: string; source?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.status) queryParams.append("status", params.status);
    if (params?.trial_type) queryParams.append("trial_type", params.trial_type);
    if (params?.source) queryParams.append("source", params.source);
    const query = queryParams.toString();
    return apiService.get<{ trials: any[]; pagination: any }>(
      `${PLATFORM_ADMIN_PREFIX}/trials${query ? `?${query}` : ""}`
    );
  },

  getTrialById: (id: string) =>
    apiService.get<{ trial: any }>(`${PLATFORM_ADMIN_PREFIX}/trials/${id}`),

  getTrialStats: () =>
    apiService.get<{ statistics: any }>(`${PLATFORM_ADMIN_PREFIX}/trials/stats`),

  startTrial: (data: any) =>
    apiService.post<{ trial: any }>(`${PLATFORM_ADMIN_PREFIX}/trials`, data),

  updateTrial: (id: string, data: any) =>
    apiService.put<{ trial: any }>(`${PLATFORM_ADMIN_PREFIX}/trials/${id}`, data),

  convertTrial: (id: string, data: { payment_id: string; plan: string }) =>
    apiService.post<{ trial: any }>(`${PLATFORM_ADMIN_PREFIX}/trials/${id}/convert`, data),

  expireTrial: (id: string) =>
    apiService.post<{ trial: any }>(`${PLATFORM_ADMIN_PREFIX}/trials/${id}/expire`, {}),

  cancelTrial: (id: string) =>
    apiService.post<{ trial: any }>(`${PLATFORM_ADMIN_PREFIX}/trials/${id}/cancel`, {}),

  expireAllTrials: () =>
    apiService.post<{ expired_count: number }>(`${PLATFORM_ADMIN_PREFIX}/trials/expire-all`, {}),
};

// Notifications endpoints
export const notificationsAPI = {
  getAll: (params?: { offset?: number; limit?: number; is_read?: boolean; type?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.offset !== undefined) queryParams.append("offset", params.offset.toString());
    if (params?.limit !== undefined) queryParams.append("limit", params.limit.toString());
    if (params?.is_read !== undefined) queryParams.append("is_read", params.is_read.toString());
    if (params?.type) queryParams.append("type", params.type);
    const query = queryParams.toString();
    return apiService.get<{
      notifications: any[];
      total: number;
      limit: number;
      offset: number;
    }>(`/api/v1/notifications${query ? `?${query}` : ""}`);
  },

  getUnreadCount: () => apiService.get<{ count: number }>("/api/v1/notifications/unread-count"),

  getById: (id: string) => apiService.get<{ notification: any }>(`/api/v1/notifications/${id}`),

  markAsRead: (id: string) =>
    apiService.patch<{ notification: any }>(`/api/v1/notifications/${id}/read`, {}),

  markAllAsRead: () => apiService.patch<{ updated: number }>("/api/v1/notifications/read-all", {}),

  delete: (id: string) => apiService.delete<{ deleted: boolean }>(`/api/v1/notifications/${id}`),

  clearAll: () => apiService.delete<{ deleted: number }>("/api/v1/notifications"),

  // Push notification endpoints
  subscribePush: (subscription: PushSubscriptionJSON, userId: string) =>
    apiService.post<{ subscribed: boolean }>("/api/v1/notifications/push/subscribe", {
      subscription,
      userId,
    }),

  unsubscribePush: () =>
    apiService.post<{ unsubscribed: boolean }>("/api/v1/notifications/push/unsubscribe", {}),
};

export default apiService;
